'use client';

import {
  CircleAlert,
  CircleCheck,
  Info,
  type LucideIcon,
  TriangleAlert,
} from 'lucide-react';
import { Toaster as Sonner, toast } from 'sonner';

import { cn } from '../lib/utils';

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * Das Zeichen der Meldung im 32-px-Kreis — subcontracts `toaster.tsx:51-52,131`
 * (`w-8 h-8 rounded-full`, Zeichen 17 px, Ton je Art).
 *
 * 🔴 ERFOLG IST GRÜN, HINWEIS IST INDIGO (Auftraggeber 06.10.2026, R-31):
 * „Diese Toast-Message, die mir was beim Speichern bestätigt, sollte eher was
 * Grünes statt Blaues anzeigen … Farben sind für solche Dinge schon
 * praktisch." subcontracts färbte ok und info gleich (petrol) — Locari trennt
 * sie: `ok` = erledigt/erfüllt, `info` = Information.
 *
 * Die Farbe steht als KLASSE am Zeichen, nicht als Regel in `shadcn-ui.css`:
 * so ist sie am DOM prüfbar (`meldung-zeichen.dom.test.tsx`). Die Geometrie
 * des sonner-Behälters (`[data-icon]`) bleibt im Stylesheet.
 * 🔴 `text-success`, nicht `text-success-text` — sonner setzt
 * `--success-text` inline am Toaster auf die Schriftfarbe der Meldung.
 */
export type MeldungsTon = 'ok' | 'info' | 'warn' | 'err';

const ZEICHEN: Record<MeldungsTon, { Bild: LucideIcon; klassen: string }> = {
  ok: { Bild: CircleCheck, klassen: 'bg-success-subtle text-success' },
  info: { Bild: Info, klassen: 'bg-tile text-primary' },
  warn: { Bild: TriangleAlert, klassen: 'bg-warning-subtle text-warning-text' },
  err: { Bild: CircleAlert, klassen: 'bg-danger-subtle text-danger' },
};

export function MeldungsZeichen({ ton }: { ton: MeldungsTon }) {
  const { Bild, klassen } = ZEICHEN[ton];

  return (
    <span
      data-meldung-ton={ton}
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-full',
        klassen,
      )}
    >
      <Bild aria-hidden size={17} />
    </span>
  );
}

/*
 * 🔴 KEIN JSX AUF MODULEBENE. Stand hier als Konstante, wurde das JSX beim
 * IMPORT ausgewertet — jedes Paket, das `@kit/ui/sonner` (auch nur über
 * `rueckmeldung.tsx`) lädt, brauchte dafür eine JSX-Laufzeit. Unter einer
 * vitest-Konfiguration ohne React-Plugin (`@kit/admin`) starb so schon der
 * Import mit `React is not defined`. Im Körper des Toasters entsteht es erst
 * beim Rendern.
 */
function zeichenJeArt() {
  return {
    success: <MeldungsZeichen ton="ok" />,
    info: <MeldungsZeichen ton="info" />,
    warning: <MeldungsZeichen ton="warn" />,
    error: <MeldungsZeichen ton="err" />,
  };
}

const Toaster = ({ ...props }: ToasterProps) => {
  // Die App ist fest hell (Phase 1 / Job 1) — kein useTheme mehr noetig.
  return (
    <Sonner
      theme="light"
      // 🔴 Blatt P1: „Unten LINKS, nicht rechts: rechts sitzen
      // Entscheidungs-Spalte und Hauptaktion." Ein Hinweis, der die
      // Primaeraktion verdeckt, kostet genau den Klick, den er ankuendigt.
      //
      // Mobil sitzt er ueber der Tab-Leiste und ist nach unten wegwischbar —
      // „nativ das erwartete Verhalten". Den Abstand zur Leiste haelt die
      // Versatz-Angabe unten.
      position="bottom-left"
      /*
       * 🔴 DER ABSTAND ZUR UNTERKANTE WIRD GEMESSEN, NICHT GERATEN. Hier stand
       * erst `bottom: 88`, dann `var(--app-tab-bar-h, 74px) + 14px` — aber nur
       * als `mobileOffset`, und den wendet sonner erst unter 600 px an:
       * zwischen 600 und 767 px (Tab-Leiste sichtbar) lag die Meldung 16 px
       * über dem Fensterrand, also IN der Leiste. Und auf Flächen mit
       * Sammelleiste oder klebendem Formular-Fuss deckte sie deren linke Hälfte.
       *
       * Seit 06.10.2026 für BEIDE Breiten `--app-unterkante` (globals.css):
       * die gemessene Höhe aller festen Leisten unten, 0 ohne Leiste.
       */
      offset={{ bottom: 'calc(var(--app-unterkante, 0px) + 16px)', left: 16 }}
      mobileOffset={{
        bottom: 'calc(var(--app-unterkante, 0px) + 14px)',
        left: 16,
        right: 16,
      }}
      swipeDirections={['bottom']}
      // P1: „Nie zwei Hinweise gleichzeitig. Ein zweiter ersetzt den ersten, er
      // stapelt nicht."
      visibleToasts={1}
      className="toaster group"
      icons={zeichenJeArt()}
      /*
       * #1329 T19b — die Meldung von subcontracts (`toaster.tsx:124-150`):
       * weisse Karte, Schatten, Zeichen im getönten Kreis. Die dunkle Form aus
       * Blatt P1 (`--meldung*`) ist entfallen — subcontracts ist der Maßstab
       * (Plan #1329, Entscheidung 1). Die Lage unten LINKS bleibt (P1: rechts
       * sitzen Entscheidungs-Spalte und Hauptaktion) — begründete Setzung.
       *
       * 🔴 FARBEN NUR ÜBER SONNERS EIGENE VARIABLEN, INLINE. Am 10.09.2026 in
       * der laufenden App gemessen: sonner definiert `--normal-bg` & Co. unter
       * demselben Selektor, den es trägt, und hängt sein Stylesheet zur
       * Laufzeit ans Ende von `document.head` — ein Überschreiben im Bündel
       * verliert. Inline schlägt jedes Stylesheet. Mit `richColors` (Layout)
       * schreibt sonner `background: var(--error-bg)` und löst damit auf den
       * Inline-Wert auf; deshalb stehen ALLE Typen hier, sonst bekäme ein
       * Fehler sonners rosa Fläche statt der weissen Karte. Die Zustandsfarbe
       * trägt das Zeichen (`MeldungsZeichen` oben).
       *
       * `classNames` wirken nicht (sonners Selektoren sind stärker) —
       * Geometrie, Handlung und Zeichen liegen deshalb in `shadcn-ui.css`.
       */
      style={
        {
          '--normal-bg': 'var(--card)',
          '--normal-text': 'var(--foreground)',
          '--normal-border': 'transparent',
          '--success-bg': 'var(--card)',
          '--success-text': 'var(--foreground)',
          '--success-border': 'transparent',
          '--info-bg': 'var(--card)',
          '--info-text': 'var(--foreground)',
          '--info-border': 'transparent',
          '--warning-bg': 'var(--card)',
          '--warning-text': 'var(--foreground)',
          '--warning-border': 'transparent',
          '--error-bg': 'var(--card)',
          '--error-text': 'var(--foreground)',
          '--error-border': 'transparent',
          '--width': '380px',
        } as React.CSSProperties
      }
      {...props}
    />
  );
};

export { Toaster, toast };
